import React from 'react'
import { css, cx } from '@emotion/css'
import { OPEN_STATUSES } from '../applications-grid/shared'
import { FormattedMessage } from 'react-intl'

interface Props {
  readonly severity: Severity
  readonly breakdown: StatusBreakdown & {
    readonly total: number
    readonly open: number
  }
  readonly onlyOpen?: boolean
}

const orderedStatuses: Status[] = [
  'reported',
  'suspicious',
  'confirmed',
  'notAProblem',
  'remediated',
  'fixed',
  'autoRemediated'
]

type Severity = 'critical' | 'high' | 'medium' | 'low' | 'note'
type Status =
  | 'reported'
  | 'suspicious'
  | 'confirmed'
  | 'notAProblem'
  | 'remediated'
  | 'fixed'
  | 'autoRemediated'

interface Message {
  readonly id: string
  readonly defaultMessage: string
}

type StatusBreakdown = { readonly [key in Status]?: number }

function getMessageBySeverity(severity: Severity): Message {
  switch (severity) {
    case 'critical':
      return {
        id: 'title_vulnSeverityTypeCritical',
        defaultMessage: 'Critical'
      }
    case 'high':
      return { id: 'title_vulnSeverityTypeHigh', defaultMessage: 'High' }
    case 'medium':
      return { id: 'title_vulnSeverityTypeMedium', defaultMessage: 'Medium' }
    case 'low':
      return { id: 'title_vulnSeverityTypeLow', defaultMessage: 'Low' }
    case 'note':
      return { id: 'title_vulnSeverityTypeNote', defaultMessage: 'Note' }
  }
}

function getMessageByStatus(status: Status): Message {
  switch (status) {
    case 'reported':
      return {
        id: 'title_vulnerabilityStatusReported',
        defaultMessage: 'Reported'
      }
    case 'suspicious':
      return {
        id: 'title_vulnerabilityStatusSuspicious',
        defaultMessage: 'Suspicious'
      }
    case 'confirmed':
      return {
        id: 'title_vulnerabilityStatusConfirmed',
        defaultMessage: 'Confirmed'
      }
    case 'notAProblem':
      return {
        id: 'title_vulnerabilityStatusNotProblem',
        defaultMessage: 'Not a Problem'
      }
    case 'remediated':
      return {
        id: 'title_vulnerabilityStatusRemediated',
        defaultMessage: 'Remediated'
      }
    case 'fixed':
      return { id: 'title_vulnerabilityStatusFixed', defaultMessage: 'Fixed' }
    case 'autoRemediated':
      return {
        id: 'title_vulnerabilityStatusAutoRemediated',
        defaultMessage: 'Remediated - Auto-Verified'
      }
  }
}

interface LabeledCountProps {
  readonly total: number
  readonly message: Message
}

const countCss = css`
  margin-right: 5px;
`
function Count({ children }) {
  return <span className={countCss} children={children} />
}

function generateLabeledCount(containerCss) {
  return function LabeledCount({ total, message }: LabeledCountProps) {
    return (
      <div className={containerCss}>
        <Count children={total} />
        <FormattedMessage {...message}>
          {(text) => <span>{text}</span>}
        </FormattedMessage>
      </div>
    )
  }
}

const labeledCountContainerCss = css`
  padding-right: 10px;
`

const TotalCount = generateLabeledCount(
  cx(
    labeledCountContainerCss,
    css`
      font-weight: bold;
      display: inherit;
      margin-bottom: 5px;
    `
  )
)
const StatusCount = generateLabeledCount(labeledCountContainerCss)

const listCss = css`
  list-style-type: none;
  padding: 0;
  margin: 0;
`

export function SeverityThermometerTooltip({
  severity,
  breakdown,
  onlyOpen = false
}: Props) {
  const statuses = orderedStatuses.filter(
    (status) =>
      (!onlyOpen || OPEN_STATUSES.indexOf(status) > -1) && breakdown[status]
  )
  const { total, open } = breakdown

  return (
    <React.Fragment>
      <TotalCount
        total={onlyOpen ? open : total}
        message={getMessageBySeverity(severity)}
      />
      <ul className={listCss}>
        {statuses.map((status, index) => (
          <li key={index}>
            <StatusCount
              total={breakdown[status] || 0}
              message={getMessageByStatus(status)}
            />
          </li>
        ))}
      </ul>
    </React.Fragment>
  )
}
